import React, { useState, useEffect } from "react";
import {
    Modal,
    Typography,
    Box,
    Button,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
    Chip,
    ListItemText,
    Checkbox,
    CircularProgress,
} from "@mui/material";
import IconButton from "../iconButton";
import { white, primary500, orange100 } from "../../lib/rebrandColors/colors";
import BuildingDetailsCard from "../buildingDetailsCard";
import { handleAddOutletsToBuilding } from "../../functions/buildings";
import DailyUsageChart from "../charts/dailyUsageChart";
import SavingsDisplay from "../savingsDisplay";
import OutletList from "../outletList";
import { fetchOrgEvents } from "../../functions/events";
import { fetchOrgSchedules } from "../../functions/schedules";
import Badge from "../badge";
import Dropdown from "../dropDown";
import CalendarContainer from "../calendarContainer";
import HideToggle from "../hideToggle";

export default function BuildingDetailsScreen({
    building,
    setSelectedBuilding,
    layoutProps,
    open,
    handleClose,
}) {
    // Not actually a modal anymore
    const [openOutletModal, setOpenOutletModal] = useState(false);
    const goBack = () => {
        setSelectedBuilding({});
        handleClose();
    };
    const closeOutletModal = () => setOpenOutletModal(false);
    const [selectedOutlets, setSelectedOutlets] = useState(
        layoutProps.outlets
            .filter(outlet => building?.devices.includes(outlet.id))
            .map(outlet => outlet.id) || []
    );
    const [previousSelectedOutlets, setPreviousSelectedOutlets] = useState(selectedOutlets);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [outletError, setOutletError] = useState(false);
    const [events, setEvents] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [scheduleLoading, setScheduleLoading] = useState(false);
    const [viewDropdownValue, setViewDropdownValue] = useState("Active Schedules");
    const [viewDropdownOpen, setViewDropdownOpen] = useState(false);
    const [hideUsage, setHideUsage] = useState(false);
    const [hideOutlets, setHideOutlets] = useState(false);
    const [hideSchedules, setHideSchedules] = useState(false);

    useEffect(() => {
        if (layoutProps.orgData[0]?.uid && building?.devices) {
            fetchOrgEvents(layoutProps.orgData[0].uid, setEvents, null, building?.devices);
        };
    }, [building]);

    useEffect(() => {
        if (layoutProps.orgData[0]?.uid && building?.devices) {
            const fetchSchedules = async () => {
                setScheduleLoading(true);
                await fetchOrgSchedules(
                    [layoutProps.orgData[0].uid],
                    setSchedules,
                    setScheduleLoading,
                    null,
                    building?.devices
                );
            };
            fetchSchedules();
        };
    }, [layoutProps.orgData, building]);

    const outletsSubmit = async () => {
        // NOTE: Ensure data refreshes with this update
        const selectedOutletObjects = layoutProps.outlets.filter(outlet => selectedOutlets.includes(outlet.id));
        const removedOutlets = layoutProps.outlets.filter(
            outlet => previousSelectedOutlets.includes(outlet.id) && !selectedOutlets.includes(outlet.id)
        );
        await handleAddOutletsToBuilding(selectedOutlets, building, layoutProps, setOutletError, setSubmitLoading, closeOutletModal, selectedOutletObjects, removedOutlets);
        // refreshes app state
        layoutProps.setRefresh(prev => !prev);
        // closeOutletModal();
    };
    const closeModal = () => {
        setOpenOutletModal(false);
        setOutletError(false);
    };

    return (
        <Modal open={open} onClose={goBack}>
            <div style={{
                position: "absolute",
                top: "2.5%",
                left: "5%",
                maxWidth: '95vW',
                width: '90vW',
                height: '95vH',
                overflowY: 'scroll',
                borderRadius: "24px",
                bgcolor: '#FAFAFA',
            }}>
                <div style={{
                    backgroundColor: "#1F1F1F",
                    minHeight: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: (hideUsage && hideSchedules && hideOutlets) ? 'start' : "center",
                    alignItems: "center",
                    paddingTop: "20px",
                    gap: "20px",
                    overflowY: 'scroll',
                }}>
                    {/* Header Section */}
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "95%",
                        maxWidth: "1200px",
                    }}>
                        <Typography sx={{
                            color: white,
                            fontFamily: "IBM Plex Sans",
                            fontSize: "32px",
                            fontWeight: 400,
                            lineHeight: "40px",
                            marginTop: '10px',
                            marginBottom: '20px'
                        }}>
                            {layoutProps.buildings.find(b => b.buildingID === building?.buildingID)?.name} Details
                        </Typography>
                        <IconButton
                            iconName='cancel-1'
                            size={35}
                            onClick={goBack} // Navigate back
                        />
                    </div>
                    {/* Details and Usage */}
                    <div style={{
                        display: "flex",
                        gap: "20px",
                        padding: "20px",
                        borderRadius: "24px",
                        maxWidth: "1250px",
                        width: "95%",
                        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                        backgroundColor: white,
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'end',
                            width: '100%',
                            alignItems: 'center'
                        }}>
                            {hideUsage &&
                                <div className="headlineMedium" style={{ width: '100%' }}>
                                    Details and Graph
                                </div>
                            }
                            <HideToggle hide={hideUsage} setHide={setHideUsage} />
                        </div>
                        {!hideUsage &&
                            <div style={{
                                display: "flex",
                                maxWidth: "1250px",
                                width: "100%",
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                            }}>

                                {/* Left Section: Building Details */}
                                <div style={{ alignSelf: 'center' }}>
                                    <BuildingDetailsCard
                                        building={building}
                                        layoutProps={layoutProps}
                                    />
                                </div>
                                {/* Right Section: Usage Graph */}
                                <Box sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    width: "520px",
                                    height: "550px",
                                    padding: "20px",
                                    borderRadius: "16px",
                                    border: "1px solid #E0E0E0",
                                    backgroundColor: "#F9F9F9",
                                }}>
                                    {/* Daily Usage Chart */}
                                    <Box sx={{
                                        marginBottom: "10px",
                                        //paddingBottom: "40px",
                                        backgroundColor: "#FFFFFF",
                                        borderRadius: "16px",
                                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                                    }}>
                                        <DailyUsageChart
                                            layoutProps={layoutProps}
                                            buildingOutlets={building?.devices}
                                            buildingID={building?.buildingID}
                                        />
                                    </Box>
                                    {/* Savings Display */}
                                    <Box sx={{
                                        padding: "16px",
                                        backgroundColor: "#FFFFFF",
                                        borderRadius: "16px",
                                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                                    }}>
                                        <SavingsDisplay
                                            layoutProps={layoutProps}
                                            buildingOutlets={building?.devices}
                                            buildingID={building?.buildingID}
                                        />
                                    </Box>
                                </Box>
                            </div>
                        }
                    </div>
                    {/* Appliances */}
                    <div style={{
                        backgroundColor: "#F9F9F9",
                        width: '100%',
                        maxWidth: "1250px",
                        padding: "20px",
                        borderRadius: "24px",
                        justifyContent: 'center',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%',
                            alignSelf: 'center'
                        }}>
                            <div className="headlineMedium">
                                Appliances
                            </div>
                            {/* outlet form */}
                            <div style={{ display: 'flex' }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'end',
                                    marginRight: '20px'
                                }}>
                                    {openOutletModal === false && !hideOutlets &&
                                        <div style={{ height: '75px' }}>
                                            <Button
                                                variant="contained"
                                                onClick={() => setOpenOutletModal(true)}
                                                sx={{
                                                    backgroundColor: primary500,
                                                    color: "#FFFFFF",
                                                    fontWeight: "bold",
                                                    textTransform: "none",
                                                    width: '175px',
                                                    padding: "8px 16px",
                                                    borderRadius: "24px",
                                                    display: "flex",
                                                    // alignItems: "center",
                                                    "&:hover": {
                                                        backgroundColor: "#D32F2F",
                                                    },
                                                    cursor: layoutProps.role === "view" ? "not-allowed" : "pointer",
                                                }}
                                                disabled={layoutProps.role === "view" ? true : false}
                                            >
                                                Edit Building Outlets
                                            </Button>
                                        </div>
                                    }
                                    {openOutletModal === true && !hideOutlets &&
                                        <div style={{ width: '100%', height: '150px' }}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'end'
                                            }}>
                                                <IconButton
                                                    iconName='cancel-1'
                                                    size={25}
                                                    onClick={() => closeModal()} // Navigate back
                                                />
                                            </div>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-evenly'
                                            }}>
                                                <FormControl sx={{
                                                    width: "785px",
                                                    marginTop: '10px',
                                                    marginRight: '10px'
                                                }}>
                                                    <Select
                                                        multiple
                                                        value={selectedOutlets}
                                                        onChange={(e) => {
                                                            setSelectedOutlets(e.target.value); // value will be an array of selected outlet IDs
                                                            setOutletError(false);
                                                        }}
                                                        required
                                                        renderValue={(selected) => (
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', overflow: 'scroll', maxHeight: '80px' }}>
                                                                {selected.map((id) => {
                                                                    const outlet = layoutProps.outlets.find(outlet => outlet.id === id);
                                                                    return outlet ? <Chip key={id} label={outlet.name} sx={{ margin: '2px' }} /> : null;
                                                                })}
                                                            </Box>
                                                        )}
                                                    >
                                                        {layoutProps.outlets.map((outlet) => (
                                                            <MenuItem key={`${outlet.id}${outlet.name}`} value={outlet.id}>
                                                                <Checkbox checked={selectedOutlets.includes(outlet.id)} />
                                                                <ListItemText primary={outlet.name} />
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    <FormHelperText sx={{ color: outletError ? 'orange' : 'inherit' }}>
                                                        {outletError
                                                            ? 'One or more outlets conflicts with another building.'
                                                            : 'Select outlets to add to the building'}
                                                    </FormHelperText>
                                                </FormControl>
                                                {submitLoading === true ?
                                                    <div style={{
                                                        width: '150px',
                                                        height: '50px',
                                                        padding: "8px 16px",
                                                        alignSelf: 'center',
                                                        justifyContent: 'center',
                                                        display: 'flex'
                                                    }}>
                                                        <CircularProgress />
                                                    </div> :
                                                    <Button
                                                        variant="contained"
                                                        onClick={outletsSubmit}
                                                        sx={{
                                                            backgroundColor: primary500,
                                                            color: "#FFFFFF",
                                                            fontWeight: "bold",
                                                            textTransform: "none",
                                                            width: '150px',
                                                            height: '50px',
                                                            padding: "8px 16px",
                                                            borderRadius: "24px",
                                                            display: "flex",
                                                            alignSelf: "center",
                                                            "&:hover": {
                                                                backgroundColor: "#D32F2F",
                                                            },
                                                            cursor: layoutProps.role === "view" ? "not-allowed" : outletError === true ? 'not-allowed' : "pointer",
                                                        }}
                                                        disabled={layoutProps.role === "view" ? true : outletError === true ? true : false}
                                                    >
                                                        Submit Outlet Changes
                                                    </Button>}
                                            </div>
                                        </div>
                                    }
                                </div>
                                <HideToggle hide={hideOutlets} setHide={setHideOutlets} />
                            </div>
                        </div>
                        {/* Outlet List */}
                        {!hideOutlets &&
                            <div style={{
                                maxWidth: "1250px",
                                display: "flex",
                                borderRadius: '24px',
                                minHeight: '693px',
                                backgroundColor: '#FAFAFA',
                                overflowY: 'hidden',
                                // marginRight: '16px',
                                justifyContent: 'center'
                            }}>
                                <OutletList
                                    width={'1000px'}
                                    height={'750px'}
                                    layoutProps={layoutProps}
                                    buildingOutlets={building?.devices}
                                    buildingID={building?.buildingID}
                                />
                            </div>
                        }
                    </div>
                    {/* Schedules */}
                    <div style={{
                        backgroundColor: "#F9F9F9",
                        width: '100%',
                        maxWidth: "1250px",
                        padding: "20px",
                        borderRadius: "24px",
                        justifyContent: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignSelf: 'center'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%',
                            alignSelf: 'center'
                        }}>
                            <div
                                className="headlineMedium"
                            >
                                Schedules
                            </div>
                            <div style={{ display: 'flex' }}>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'end',
                                    marginRight: '20px'
                                }}>
                                    {!hideSchedules &&
                                        <div style={{
                                            width: '33%',
                                            display: 'flex',
                                            justifyContent: 'end',
                                        }}>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                {schedules?.filter(
                                                    (schedule) => schedule.status === "New"
                                                ).length > 0 && viewDropdownValue === 'Active Schedules' &&
                                                    <div style={{
                                                        position: 'absolute',
                                                        marginTop: '17px',
                                                        marginLeft: '-2.5px'
                                                    }}>
                                                        <Badge />
                                                    </div>
                                                }
                                                <Dropdown
                                                    list={["Active Schedules", "Recommended Schedules"]}
                                                    value={viewDropdownValue}
                                                    onSelect={(item) => setViewDropdownValue(item)}
                                                    setIsDropdownOpen={setViewDropdownOpen}
                                                    isDropdownOpen={viewDropdownOpen}
                                                    label={"Schedule type"}
                                                />
                                            </div>
                                        </div>
                                    }
                                </div>
                                <HideToggle hide={hideSchedules} setHide={setHideSchedules} />
                            </div>
                        </div>
                        {!hideSchedules &&
                            (viewDropdownValue === "Active Schedules" ? (
                                <div style={{ marginTop: "30px", marginBottom: "20px", }}>
                                    <CalendarContainer
                                        layoutProps={layoutProps}
                                        filteredEvents={events}
                                        filteredSchedules={schedules}
                                        filter="Accepted"
                                        building={building}
                                    />
                                </div>
                            ) : (
                                <div style={{ marginTop: "30px", marginBottom: "20px" }}>
                                    <CalendarContainer
                                        layoutProps={layoutProps}
                                        filteredEvents={events}
                                        filteredSchedules={schedules?.filter(
                                            (schedule) => schedule.status === "New"
                                        )}
                                        filter="New"
                                        building={building}
                                    />
                                </div>
                            ))
                        }
                    </div>
                </div>
            </div>
        </Modal>
    )
};